import { useRef, useState } from 'react';
import { cn } from '../lib/cn';

interface UploadDropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export function UploadDropzone({ onFiles, disabled }: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []).filter((file) => file.type.includes('audio'));
    if (files.length) onFiles(files);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          inputRef.current?.click();
        }
      }}
      onClick={() => inputRef.current?.click()}
      onDragEnter={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        handleFiles(event.dataTransfer.files);
      }}
      className={cn(
        'panel flex min-h-72 cursor-pointer flex-col items-center justify-center rounded-[2rem] border-2 border-dashed px-6 text-center transition',
        isDragging ? 'border-accent bg-accent/10' : 'border-white/15',
        disabled && 'cursor-not-allowed opacity-60'
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".mp3,.wav,.m4a,audio/*"
        multiple
        hidden
        disabled={disabled}
        onChange={(event) => handleFiles(event.target.files)}
      />
      <p className="pill">Batch import</p>
      <h2 className="mt-6 font-display text-3xl font-semibold">Drop songs anywhere here</h2>
      <p className="mt-3 max-w-xl text-sm text-foreground/70">
        Import multiple files at once. Local Wave extracts ID3 tags, album art, and stores the
        audio securely in IndexedDB for offline playback.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3 text-xs text-foreground/60">
        <span className="pill">MP3 metadata parsing</span>
        <span className="pill">Artwork extraction</span>
        <span className="pill">Deduping by content hash</span>
      </div>
    </div>
  );
}
